/**
 * Details — collapsible disclosure block with an accent strip.
 *
 * Replaces the MkDocs `??? type "title"` syntax with a typed React
 * component wrapping the DS Collapsible (Radix-backed). The trigger lives
 * outside the .prose context (the chevron is a UI control, not typographic
 * content), but the body opts back into <Prose> so paragraphs, lists, and
 * links inside the disclosure match the ambient article styling.
 *
 * Usage:
 *   <Details variant="info" title="CI/CD — 16 GitHub Actions Workflows">
 *     Trunk-based development, PRs required, 16 workflows covering…
 *   </Details>
 */

import type { ComponentType, ReactNode, SVGProps } from "react";
import { ChevronDown, HelpCircle, Info } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@taiga-platform/ui";

export type DetailsVariant = "note" | "info" | "success" | "question";

interface VariantStyle {
  accent: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
}

const VARIANTS: Record<DetailsVariant, VariantStyle> = {
  note: { accent: "text-foreground" },
  info: { accent: "text-info", icon: Info },
  success: { accent: "text-success" },
  question: { accent: "text-primary", icon: HelpCircle },
};

interface DetailsProps {
  title: string;
  variant?: DetailsVariant;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function Details({
  title,
  variant = "note",
  defaultOpen = false,
  children,
}: DetailsProps) {
  const { accent, icon: LeadIcon } = VARIANTS[variant];

  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className="not-prose my-3 overflow-hidden rounded-lg border border-border bg-card"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group flex w-full items-center gap-3 px-4 py-3 text-left font-medium text-foreground transition-colors hover:bg-muted/50"
        >
          {LeadIcon && (
            <LeadIcon
              className={`size-4 shrink-0 ${accent}`}
              aria-hidden="true"
            />
          )}
          <span className={`flex-1 min-w-0 ${accent}`}>{title}</span>
          <ChevronDown
            className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-t border-border px-4 py-3 text-sm text-foreground [&_p]:mt-2 [&_p:first-child]:mt-0 [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-1">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
